"use client";

import { usePostHog } from "posthog-js/react";
import * as React from "react";

import {
  appendAppLinkAdClickIdsToUrl,
  getAdClickIdsFromUrl,
  resolveAppLinkAdClickIds,
} from "@/lib/ad-click-ids";
import rootLogger from "@/lib/logging";

const moduleLogger = rootLogger.child({ module: "ui/link/external-link" });

const ExternalLink = React.forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement>
>(({ children, href, onClick, ...props }, ref) => {
  const [updatedHref, setUpdatedHref] = React.useState(href);
  const posthog = usePostHog();
  const isAppLink = href?.includes("app.wayflyer");

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (isAppLink && posthog?.__loaded && posthog.has_opted_in_capturing()) {
        posthog.createPersonProfile();
      }
      onClick?.(event);
    },
    [isAppLink, posthog, onClick]
  );

  React.useEffect(() => {
    // Append ad platform click IDs to Wayflyer app links for conversion attribution.
    // session_id and distinct_id are NOT needed in the URL:
    // - Consenting users: shared PostHog cookie via .wayflyer.com domain scope
    // - Non-consenting users: cookieless server-side hash uses root domain,
    //   so wayflyer.com and app.wayflyer.com resolve to the same distinct_id
    if (posthog && posthog.__loaded && href?.includes("app.wayflyer")) {
      try {
        const clickIds = resolveAppLinkAdClickIds(
          getAdClickIdsFromUrl(),
          (key) => posthog.get_property?.(key)
        );
        const hasAny = Object.keys(clickIds).length > 0;
        setUpdatedHref(
          hasAny ? appendAppLinkAdClickIdsToUrl(href, clickIds) : href
        );
      } catch (error) {
        moduleLogger.error(
          { href, error },
          "Error adding ad click IDs to Wayflyer app link"
        );
      }
    }
  }, [posthog, posthog.__loaded, href]);

  return (
    <a ref={ref} href={updatedHref} onClick={handleClick} {...props}>
      {children}
    </a>
  );
});

ExternalLink.displayName = "ExternalLink";

export { ExternalLink };
